import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CROWD_ROLES } from '@vigilo/security';
import { post, TOKEN_KEY } from '@vigilo/api-client';
import { useApp } from '../../shell/AppContext';
import { useMockMode } from '../../shell/MockModeContext';

export const CROWD_CLIENT_ACTIVITY_ACTION = {
  pageViewed: 'crowd.page.viewed',
  filterChanged: 'crowd.filter.changed',
  searchPerformed: 'crowd.search.performed',
  recordViewed: 'crowd.record.viewed',
  operationSelected: 'crowd.operation.selected',
  tabChanged: 'crowd.tab.changed',
  drilldownOpened: 'crowd.drilldown.opened',
  mapLayerChanged: 'crowd.map.layer.changed',
  mapControlChanged: 'crowd.map.control.changed',
  playbackChanged: 'crowd.playback.changed',
  reportTemplateSelected: 'crowd.report.template.selected',
} as const;

type CrowdClientActivityAction =
  typeof CROWD_CLIENT_ACTIVITY_ACTION[keyof typeof CROWD_CLIENT_ACTIVITY_ACTION];

type FilterValue = string | number | boolean | null | undefined;
type MetadataValue = string | number | boolean | null | undefined;

export const CROWD_CLIENT_RESOURCE_TYPE = {
  pageView: 'crowd.page_view',
  operation: 'crowd.operation',
  zone: 'crowd.zone',
  venue: 'crowd.venue',
  alert: 'crowd.alert',
  anonymousFlag: 'crowd.anonymous_flag',
  report: 'crowd.report',
  mapLayer: 'crowd.map_layer',
  mapControl: 'crowd.map_control',
  playback: 'crowd.playback',
  uiTab: 'crowd.ui_tab',
  drilldown: 'crowd.drilldown',
} as const;

type CrowdClientResourceType =
  typeof CROWD_CLIENT_RESOURCE_TYPE[keyof typeof CROWD_CLIENT_RESOURCE_TYPE];

interface CrowdActivityInput {
  action: CrowdClientActivityAction;
  page: string;
  operationId?: string | null;
  resourceType?: CrowdClientResourceType;
  resourceId?: string | null;
  filterKeys?: string[];
  filterValues?: Record<string, FilterValue>;
  searchApplied?: boolean;
  metadata?: Record<string, MetadataValue>;
}

interface FilterActivityInput {
  page: string;
  operationId?: string | null;
  filterValues: Record<string, FilterValue>;
  searchApplied?: boolean;
  enabled?: boolean;
}

const ACTIVITY_DEBOUNCE_MS = 450;

export function useCrowdActivityAudit(): (input: CrowdActivityInput) => void {
  const { currentUser, selectedOperation } = useApp();
  const { isMock } = useMockMode();

  return useCallback((input: CrowdActivityInput) => {
    const token = localStorage.getItem(TOKEN_KEY());
    if (isMock || !token || !CROWD_ROLES.has(String(currentUser?.role ?? ''))) return;
    const filterValues = sanitizedFilterValues(input.filterValues);
    const filterKeys = [
      ...new Set([
        ...(input.filterKeys ?? []),
        ...Object.keys(filterValues),
        ...(input.searchApplied ? ['search'] : []),
      ]),
    ].sort();

    void post('/crowd/activity', {
      action: input.action,
      page: input.page,
      operationId: input.operationId ?? selectedOperation?.id,
      resourceType: input.resourceType,
      resourceId: input.resourceId,
      filterKeys,
      filterValues,
      searchApplied: input.searchApplied,
      metadata: sanitizedMetadata(input.metadata),
    }).catch(() => undefined);
  }, [currentUser?.role, isMock, selectedOperation?.id]);
}

export function useCrowdFilterActivity(input: FilterActivityInput): void {
  const recordActivity = useCrowdActivityAudit();
  const initialized = useRef(false);
  const previousPayload = useRef<{
    page: string;
    operationId: string | null;
    filterValues: Record<string, string>;
    searchApplied: boolean;
  } | null>(null);
  const signature = useMemo(() => JSON.stringify({
    page: input.page,
    operationId: input.operationId ?? null,
    filterValues: sanitizedFilterValues(input.filterValues),
    searchApplied: Boolean(input.searchApplied),
  }), [input.filterValues, input.operationId, input.page, input.searchApplied]);

  useEffect(() => {
    if (input.enabled === false) return;
    const payload = JSON.parse(signature) as {
      page: string;
      operationId: string | null;
      filterValues: Record<string, string>;
      searchApplied: boolean;
    };
    if (!initialized.current) {
      initialized.current = true;
      previousPayload.current = payload;
      return;
    }
    const previous = previousPayload.current;
    previousPayload.current = payload;
    const filterKeys = activityKeys(payload);
    const previousKeys = previous ? activityKeys(previous) : [];
    if (!filterKeys.length && !payload.searchApplied && !previousKeys.length) return;

    const timeout = window.setTimeout(() => {
      recordActivity({
        action: payload.searchApplied
          ? CROWD_CLIENT_ACTIVITY_ACTION.searchPerformed
          : CROWD_CLIENT_ACTIVITY_ACTION.filterChanged,
        page: payload.page,
        operationId: payload.operationId,
        filterKeys: filterKeys.length ? filterKeys : previousKeys,
        filterValues: payload.filterValues,
        searchApplied: payload.searchApplied,
      });
    }, ACTIVITY_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [input.enabled, recordActivity, signature]);
}

function sanitizedFilterValues(values?: Record<string, FilterValue>): Record<string, string> {
  const sanitized: Record<string, string> = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'search') continue;
    sanitized[key] = String(value);
  }
  return sanitized;
}

function sanitizedMetadata(values?: Record<string, MetadataValue>): Record<string, string | number | boolean> {
  const sanitized: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (/note|reason|search|token|coordinate|geometry|payload/i.test(key)) continue;
    if (typeof value === 'number' && Number.isFinite(value)) {
      sanitized[key] = value;
    } else if (typeof value === 'boolean') {
      sanitized[key] = value;
    } else if (typeof value === 'string') {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

function activityKeys(payload: { filterValues: Record<string, string>; searchApplied: boolean }): string[] {
  return [
    ...new Set([
      ...Object.keys(payload.filterValues),
      ...(payload.searchApplied ? ['search'] : []),
    ]),
  ].sort();
}
